import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeaturesI18N, MsgBoxComponent } from 'controls';
import { CustomSnackBarData, CustomSnackBarType, CustomSnackbarComponent } from '../view/control/custom-snackbar/custom-snackbar.component';

type SnackOptions = { duration?: number; autoclose?: boolean; warn?: boolean };

export enum DialogResult {
    YES,
    NO,
    CANCEL,
}

@Injectable()
export class DialogService {
    private closer = new Subject<void>();

    constructor(private dialog: MatDialog, private snackBar: MatSnackBar) {}

    confirmValidationErrors(title: string, list: string[]): Observable<boolean> {
        const ok = FeaturesI18N.getString('dialog.button.ok');
        const cancel = FeaturesI18N.getString('dialog.button.cancel');
        const data = { title, list, buttons: [cancel, ok] };

        return this.dialog
            .open(MsgBoxComponent, { data, width: '100%' })
            .afterClosed()
            .pipe(map((result) => result === 2));
    }

    confirmSaveAction(): Observable<DialogResult> {
        const yes = FeaturesI18N.getString('dialog.button.yes');
        const no = FeaturesI18N.getString('dialog.button.no');
        const cancel = FeaturesI18N.getString('dialog.button.cancel');
        const caption = FeaturesI18N.getString('dialog.title.confirmation');
        const message = FeaturesI18N.getString('confirmation.save');
        const data = { title: caption, content: message, buttons: [yes, no, cancel] };

        return this.dialog
            .open(MsgBoxComponent, { data, width: '100%' })
            .afterClosed()
            .pipe(map((result) => this.mapDialogResult(result)));
    }

    notifyInMessageBox(title: string, content: string): Observable<boolean> {
        const ok = FeaturesI18N.getString('dialog.button.ok');
        const data = { title, content, buttons: [ok] };

        return this.dialog
            .open(MsgBoxComponent, { data, width: '100%' })
            .afterClosed()
            .pipe(map((result) => Boolean(result)));
    }

    askQuestion(title: string, content: string): Observable<boolean> {
        const ok = FeaturesI18N.getString('dialog.button.ok');
        const cancel = FeaturesI18N.getString('dialog.button.cancel');
        const data = { title, content, buttons: [cancel, ok] };

        return this.dialog
            .open(MsgBoxComponent, { data, width: '100%' })
            .afterClosed()
            .pipe(map((result) => result === 2));
    }

    notifyInSnackBar(message: string, type: CustomSnackBarType = 'default', options?: SnackOptions): Observable<void> {
        const config: MatSnackBarConfig<CustomSnackBarData> = {
            data: { message, type, snackBar: this.snackBar },
            duration: options ? options.duration : message.length * 200,
            panelClass: ['custom-snackbar'],
        };

        return this.snackBar.openFromComponent(CustomSnackbarComponent, config).afterOpened();
    }

    closeOpenedNotifications(): void {
        this.closer.next();
        this.snackBar.dismiss();
    }

    private mapDialogResult<T>(value: T): DialogResult {
        switch (value) {
            case 1:
                return DialogResult.YES;
            case 2:
                return DialogResult.NO;
            case 3:
                return DialogResult.CANCEL;
            default:
                return DialogResult.CANCEL;
        }
    }
}
